import { ReactNode } from 'react';
import { formatNumber } from '../utils/csvParser';

export type FleetKpiAccent = 'green' | 'blue' | 'amber' | 'purple';

const ACCENT: Record<
  FleetKpiAccent,
  { grad: string; border: string; blob: string; dot: string }
> = {
  green: {
    grad: 'from-success/20 via-card to-background',
    border: 'border-success/30',
    blob: 'bg-success/10',
    dot: 'bg-success',
  },
  blue: {
    grad: 'from-ring/20 via-card to-background',
    border: 'border-ring/30',
    blob: 'bg-ring/10',
    dot: 'bg-ring',
  },
  amber: {
    grad: 'from-warning/20 via-card to-background',
    border: 'border-warning/30',
    blob: 'bg-warning/10',
    dot: 'bg-warning',
  },
  purple: {
    grad: 'from-brand/20 via-card to-background',
    border: 'border-brand/30',
    blob: 'bg-brand/10',
    dot: 'bg-brand',
  },
};

export interface FleetStyleKpiCardProps {
  title: string;
  value: ReactNode;
  subtitle?: ReactNode;
  accent: FleetKpiAccent;
  trend?: { value: number; isPositive: boolean };
  className?: string;
}

/**
 * KPI tile matching Fleet Report: gradient panel, accent border, pulsing dot, large value.
 */
export default function FleetStyleKpiCard({
  title,
  value,
  subtitle,
  accent,
  trend,
  className = '',
}: FleetStyleKpiCardProps) {
  const a = ACCENT[accent];
  return (
    <div
      className={`relative overflow-hidden bg-gradient-to-br ${a.grad} rounded-xl border ${a.border} p-6 shadow-xl ${className}`}
    >
      <div className={`absolute top-0 right-0 w-32 h-32 ${a.blob} rounded-full -mr-16 -mt-16 blur-2xl`} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-2 h-2 ${a.dot} rounded-full animate-pulse`} />
          <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{title}</div>
        </div>
        <div className="text-4xl font-bold text-white mb-1 break-words">{value}</div>
        {subtitle != null && subtitle !== '' && (
          <div className="text-xs text-muted-foreground font-medium">{subtitle}</div>
        )}
        {trend != null && (
          <div className="flex items-center mt-1">
            <span
              className={`text-xs font-medium ${
                trend.isPositive ? 'text-success' : 'text-destructive'
              }`}
            >
              {trend.isPositive ? '↑' : '↓'} {formatNumber(Math.abs(trend.value), 2)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
